import { computed, onUnmounted, ref, watch, type ShallowRef } from 'vue'

import { useRouteModeShortcuts } from '@/composables/useRouteModeShortcuts'
import { useTrackEditInsertDrag } from '@/composables/useTrackEditInsertDrag'
import { useTrackRoutingSession } from '@/composables/useTrackRoutingSession'
import { buildLineStringFromSegments, parseLineString } from '@/services/lineStringService'
import type { TrackRoutingAdapter } from '@/services/trackRoutingAdapter'
import { useUiStore } from '@/stores/uiStore'
import type { Segment, Tour } from '@/types/tour'
import { resolveTrackOverlayColor } from '@/utils/trackOverlayColor'

import type maplibregl from 'maplibre-gl'
import type { MapMouseEvent } from 'maplibre-gl'

export interface UseTrackRoutingHostOptions {
  adapter: TrackRoutingAdapter
  mapInstance: ShallowRef<maplibregl.Map | null>
  /** Returns the MapLibre layer id used as drag-insert touch target for the tour-track. */
  getTouchTargetLayerId: (tourId: string, trackClientId: string) => string | null
  /** Called when an idle (non-drawing, non-edit) map click occurs. Return true to consume. */
  onIdleMapClick?: (event: MapMouseEvent) => boolean
}

/**
 * Shared workspace host glue for the route draw/edit flow.
 * `WorkspaceMapContainer` uses this to avoid duplicated wiring between
 * `useTrackRoutingSession`, shortcuts, edit-insert drag, and map-click dispatch.
 *
 * Host-specific concerns that remain in the view:
 *  - Adapter construction.
 *  - `start-track-drawing` event listener and target-tour resolution.
 *  - `enterTrackEditModeForTrack`.
 *  - Mobile/panel visibility side effects (pass via `onIdleMapClick` hook).
 */
export function useTrackRoutingHost(options: UseTrackRoutingHostOptions) {
  const { adapter, mapInstance, getTouchTargetLayerId, onIdleMapClick } = options
  const uiStore = useUiStore()
  const ROUTING_DOUBLE_CLICK_DELAY_MS = 250

  const drawingTrackPoints = ref<[number, number][]>([])
  const drawingTrackSegments = ref<Segment[]>([])
  const editReferenceCoordinates = ref<[number, number][]>([])
  const editReferenceTrackSegments = ref<Segment[]>([])
  const lastClickedPoint = ref<[number, number] | null>(null)
  const isCalculatingRoute = ref(false)
  let pendingRoutingMapClickTimer: ReturnType<typeof setTimeout> | null = null

  const trackEditInsertDragState: { instance: ReturnType<typeof useTrackEditInsertDrag> | null } = {
    instance: null,
  }

  const trackRoutingSession = useTrackRoutingSession({
    mapInstance,
    drawingTrackPoints,
    drawingTrackSegments,
    lastClickedPoint,
    isCalculatingRoute,
    shouldSuppressInsertDragClick: () =>
      trackEditInsertDragState.instance?.shouldSuppressClick() ?? false,
    adapter,
  })

  const trackDrawing = trackRoutingSession.drawing
  const trackEdit = trackRoutingSession.edit
  const activeDraftTourRef = trackRoutingSession.activeDraftTour ?? ref<Tour | null>(null)
  const getRouteModeTour = (tourId: string | null | undefined): Tour | null => {
    if (!tourId) return null
    const draftTour = activeDraftTourRef.value
    return draftTour?.clientId === tourId ? draftTour : adapter.getTour(tourId)
  }

  const {
    isTrackDragging,
    shouldSuppressDragClick,
    trackClientId: drawingTrackClientId,
    pendingRoutePoints: drawingPendingRoutePoints,
    canUndoTrackDrawing,
    canRedoTrackDrawing,
    canSaveTrackDrawing,
    cancelTrackDragging,
    undoLastSegment,
    redoLastSegment,
    closeTrackDrawing,
    saveTrackDrawing,
    addDrawingRoutePoint,
    discardTrackDrawing = closeTrackDrawing,
  } = trackDrawing

  const {
    sparseTrackSetup,
    sparseTrackPreviewRoutePoints,
    isSparseTrackSetupOpen,
    isApplyingSparseTrackSetup,
    canUndoTrackEdit,
    canRedoTrackEdit,
    canSaveTrackEdit,
    markRoutePointChanged,
    closeTrackEdit: closeTrackEditSession,
    saveTrackEdit,
    discardTrackEdit = closeTrackEditSession,
    maybeOpenSparseTrackSetup,
    closeSparseTrackSetup,
    updateSparseTrackPointCount,
    confirmSparseTrackSetup,
    handleTrackEditClick,
    handleRoutePointDragend: handleTrackEditRoutePointDragend,
    handleTrackEditInsertDrop: handleTrackEditInsertDropAction,
    undoEditModeSegment,
    redoTrackEdit,
    handlePendingDeleted,
    handlePendingDragend,
    initEditModeLastClickedPoint,
    resetEditState,
    justEnteredEditMode,
    markJustEnteredEditMode,
  } = trackEdit

  const captureEditReferenceTrack = (
    track: { pointsLineReduced5m?: string | null; segments?: Segment[] | null } | null | undefined,
  ) => {
    if (!track) {
      editReferenceCoordinates.value = []
      editReferenceTrackSegments.value = []
      return
    }
    const lineString =
      track.pointsLineReduced5m ??
      (Array.isArray(track.segments) ? buildLineStringFromSegments(track.segments) : undefined)
    editReferenceCoordinates.value = lineString ? parseLineString(lineString) : []
    editReferenceTrackSegments.value = Array.isArray(track.segments) ? [...track.segments] : []
  }

  const clearEditReferenceTrack = () => {
    editReferenceCoordinates.value = []
    editReferenceTrackSegments.value = []
  }

  const closeTrackEdit = async () => {
    await closeTrackEditSession()
    if (!uiStore.isTrackEditMode) {
      clearEditReferenceTrack()
    }
  }

  const saveAndCloseTrackEdit = async () => {
    await saveTrackEdit()
    if (!uiStore.isTrackEditMode) {
      clearEditReferenceTrack()
    }
  }

  const hasEditingTrack = computed(() => {
    if (!uiStore.isTrackEditMode || !uiStore.editingTrackTourId || !uiStore.editingTrackClientId) {
      return true
    }

    const tour = getRouteModeTour(uiStore.editingTrackTourId)
    if (!tour) return false

    return tour.tracks.some((track) => track.clientId === uiStore.editingTrackClientId)
  })

  watch(
    () => uiStore.isTrackEditMode,
    (isTrackEditMode) => {
      if (!isTrackEditMode) {
        clearEditReferenceTrack()
      }
    },
    { immediate: true },
  )

  watch(hasEditingTrack, (exists) => {
    if (!uiStore.isTrackEditMode || exists) return
    void closeTrackEdit()
  })

  useRouteModeShortcuts({
    isTrackDragging: () => isTrackDragging.value,
    cancelTrackDragging,
    canUndoTrackDrawing: () => canUndoTrackDrawing.value,
    undoTrackDrawing: undoLastSegment,
    canRedoTrackDrawing: () => canRedoTrackDrawing.value,
    redoTrackDrawing: redoLastSegment,
    closeTrackDrawing,
    canUndoTrackEdit: () => canUndoTrackEdit.value,
    undoTrackEdit: undoEditModeSegment,
    canRedoTrackEdit: () => canRedoTrackEdit.value,
    redoTrackEdit,
    closeTrackEdit,
  })

  const resolveEditingTrackIndex = (): number | null => {
    const tourId = uiStore.editingTrackTourId
    const trackClientId = uiStore.editingTrackClientId
    if (!tourId || !trackClientId) return null
    const tour = getRouteModeTour(tourId)
    if (!tour) return null
    const idx = tour.tracks.findIndex((t) => t.clientId === trackClientId)
    return idx >= 0 ? idx : null
  }

  const getEditingTour = (): Tour | null => {
    const tourId = uiStore.editingTrackTourId
    if (!tourId) return null
    return getRouteModeTour(tourId)
  }

  const handleTrackEditInsertDrop = async (insertIndex: number, lat: number, lon: number) => {
    await handleTrackEditInsertDropAction(insertIndex, lat, lon)
  }

  trackEditInsertDragState.instance = useTrackEditInsertDrag({
    mapInstance,
    getTouchTargetLayerId: () => {
      if (!uiStore.isTrackEditMode) return null
      const tourId = uiStore.editingTrackTourId
      const trackClientId = uiStore.editingTrackClientId
      if (!tourId || !trackClientId) return null
      return getTouchTargetLayerId(tourId, trackClientId)
    },
    getEditingTour,
    getEditingTrackIndex: () => resolveEditingTrackIndex(),
    onInsert: handleTrackEditInsertDrop,
  })
  const trackEditInsertDrag = trackEditInsertDragState.instance

  const activeDrawingOverlayColor = computed(() => {
    if (uiStore.isTrackEditMode) {
      const tour = getRouteModeTour(uiStore.editingTrackTourId)
      return resolveTrackOverlayColor(tour?.tracks, uiStore.editingTrackClientId)
    }

    if (!uiStore.isTrackDrawingActive) {
      return undefined
    }

    const targetTourId = adapter.resolveTargetTourId()
    const targetTour = getRouteModeTour(targetTourId)
    return resolveTrackOverlayColor(targetTour?.tracks, drawingTrackClientId.value)
  })

  const clearPendingRoutingMapClick = () => {
    if (pendingRoutingMapClickTimer === null) return
    clearTimeout(pendingRoutingMapClickTimer)
    pendingRoutingMapClickTimer = null
  }

  const queueRoutingMapClick = (action: () => Promise<void> | void) => {
    clearPendingRoutingMapClick()
    pendingRoutingMapClickTimer = setTimeout(() => {
      pendingRoutingMapClickTimer = null
      void action()
    }, ROUTING_DOUBLE_CLICK_DELAY_MS)
  }

  const handleMapClick = async (event: MapMouseEvent) => {
    if (uiStore.shouldSuppressNextMapClick()) return

    if (!uiStore.isTrackDrawingActive && !uiStore.isTrackEditMode) {
      if (onIdleMapClick?.(event)) return
    }

    const clickCount =
      typeof event.originalEvent?.detail === 'number' ? event.originalEvent.detail : 1
    if (clickCount > 1) {
      clearPendingRoutingMapClick()
      return
    }

    if (uiStore.isTrackEditMode) {
      if (isCalculatingRoute.value) return
      if (shouldSuppressDragClick()) return
      if (justEnteredEditMode.value) return
      queueRoutingMapClick(async () => {
        if (!uiStore.isTrackEditMode) return
        if (isCalculatingRoute.value) return
        if (shouldSuppressDragClick()) return
        if (justEnteredEditMode.value) return
        await handleTrackEditClick(event)
      })
      return
    }

    queueRoutingMapClick(async () => {
      if (!uiStore.isTrackDrawingActive) return
      await trackDrawing.handleDrawingClick(event)
    })
  }

  const handleRoutePointDragend = async (
    tourId: string,
    rpIndex: number,
    newLat: number,
    newLon: number,
  ) => {
    await handleTrackEditRoutePointDragend(tourId, rpIndex, newLat, newLon)
  }

  onUnmounted(() => {
    clearPendingRoutingMapClick()
    discardTrackDrawing()
    discardTrackEdit()
  })

  return {
    trackRoutingSession,
    trackDrawing,
    trackEdit,
    // drawing state
    drawingTrackPoints,
    drawingTrackSegments,
    drawingTrackClientId,
    drawingPendingRoutePoints,
    canUndoTrackDrawing,
    canRedoTrackDrawing,
    canSaveTrackDrawing,
    isTrackDragging,
    isCalculatingRoute,
    lastClickedPoint,
    // edit state
    sparseTrackSetup,
    sparseTrackPreviewRoutePoints,
    isSparseTrackSetupOpen,
    isApplyingSparseTrackSetup,
    canUndoTrackEdit,
    canRedoTrackEdit,
    canSaveTrackEdit,
    justEnteredEditMode,
    editReferenceCoordinates,
    editReferenceTrackSegments,
    activeDraftTour: activeDraftTourRef,
    // actions
    closeTrackDrawing,
    closeTrackEdit,
    saveTrackDrawing,
    saveTrackEdit: saveAndCloseTrackEdit,
    captureEditReferenceTrack,
    undoLastSegment,
    redoLastSegment,
    addDrawingRoutePoint,
    undoEditModeSegment,
    redoTrackEdit,
    cancelTrackDragging,
    markRoutePointChanged,
    maybeOpenSparseTrackSetup,
    closeSparseTrackSetup,
    updateSparseTrackPointCount,
    confirmSparseTrackSetup,
    handleMapClick,
    handleRoutePointDragend,
    handleTrackEditInsertDrop,
    handlePendingDeleted,
    handlePendingDragend,
    initEditModeLastClickedPoint,
    resetEditState,
    markJustEnteredEditMode,
    // derived
    activeDrawingOverlayColor,
    resolveEditingTrackIndex,
    getEditingTour,
    trackEditInsertDrag,
  } as const
}
